/**
 * route-checkout-subtotal-update.js
 *
 * Route's cloned checkout button displays only the cart subtotal, omitting
 * the cost of Route Package Protection. This script sums both values so the
 * checkout button accurately reflects the total the customer will be charged.
 *
 * Strategy:
 *   - CSS hides the cloned button's price span before it ever paints, preventing
 *     a one-frame flash of the wrong value. The span is revealed only after
 *     reconcile() has written the correct combined total.
 *   - interceptInnerHTML() catches Route's button rewrites synchronously.
 *   - watchSubtotal() and watchRoutePrice() attach targeted MutationObservers
 *     directly on the two price sources so reconcile() fires immediately on
 *     any change without scanning the full cart drawer.
 *   - The outer observer's only job is detecting the cloned button's first
 *     appearance.
 *
 * Dependencies:
 *   - Requires Route to be installed (detected via #route-widget-observer-root).
 *   - Requires window.Shopify.formatMoney and window.theme.moneyFormat for
 *     correct currency formatting, with Intl.NumberFormat as fallback.
 * 
 * Implementation:
 *   <!-- Route Code Edited 6/22/26 -->
 *   <script src="{{ 'route-checkout-subtotal-update.js' | asset_url }}" defer></script>
 *   <!-- End Route Code -->
 */
(function () {
  const OBSERVER_ROOT_SELECTOR   = '#xboost-upsell-slide-cart-root';
  const SUBTOTAL_SOURCE_SELECTOR = '.xb_slide_cart_footer > .xb_slide_cart_button_checkout span span span';
  const WRITE_TARGET_SELECTOR    = '[data-route-cloned-button="true"] span span span';
  const CLONED_BUTTON_SELECTOR   = '[data-route-cloned-button="true"]';
  const ROUTE_PRICE_SELECTOR     = '#pw-coverage-description';
  const ROUTE_ROOT_ID            = 'route-widget-observer-root';
  const PRICE_CORRECTED_ATTR     = 'data-route-price-corrected';

  let outerObserver      = null;
  let guardObserver      = null;
  let subtotalObserver   = null;
  let routePriceObserver = null;
  let lastKnownGood      = null;

  // ─── Formatting ────────────────────────────────────────────────────────────

function formatLikeSubtotal(cents) {
  const subtotalEl = document.querySelector(SUBTOTAL_SOURCE_SELECTOR);
  if (subtotalEl) {
    const [integer, decimal = '00'] = (cents / 100).toFixed(2).split('.');
    const amount = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + decimal;
    return subtotalEl.textContent.replace(/[\d,]+\.\d+/, amount);
    // "$ 120.00" → "$ 122.95", not "$122.95"
  }
  // Fallback if subtotal element is gone for some reason
  if (
    window.Shopify &&
    typeof window.Shopify.formatMoney === 'function' &&
    window.theme &&
    window.theme.moneyFormat
  ) {
    return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

  function parseToCents(str) {
    if (!str) return null;
    const match = str.match(/[\d,]+\.?\d*/);
    if (!match) return null;
    const float = parseFloat(match[0].replace(/,/g, ''));
    if (isNaN(float)) return null;
    return Math.round(float * 100);
  }

  function getRoutePriceCents() {
    const el = document.querySelector(ROUTE_PRICE_SELECTOR);
    return el ? parseToCents(el.textContent) : null;
  }

  function getSubtotalCents() {
    const el = document.querySelector(SUBTOTAL_SOURCE_SELECTOR);
    return el ? parseToCents(el.textContent) : null;
  }

  function getButtonPrefix(button) {
    let prefix = '';
    button.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) prefix += node.textContent;
    });
    return prefix.trim();
  }

  // ─── Guard ─────────────────────────────────────────────────────────────────

  function attachGuard() {
    const clonedButton = document.querySelector(CLONED_BUTTON_SELECTOR);
    if (!clonedButton) return;
    if (guardObserver) guardObserver.disconnect();

    guardObserver = new MutationObserver(() => {
      if (lastKnownGood === null) return;
      const writeTarget = document.querySelector(WRITE_TARGET_SELECTOR);
      if (!writeTarget) return;
      if (writeTarget.textContent.trim() !== lastKnownGood) {
        writeTarget.textContent = lastKnownGood;
      }
    });

    guardObserver.observe(clonedButton, { subtree: true, childList: true });
  }

  // ─── Reconcile ─────────────────────────────────────────────────────────────

  function reconcile() {
    const writeTarget  = document.querySelector(WRITE_TARGET_SELECTOR);
    const clonedButton = document.querySelector(CLONED_BUTTON_SELECTOR);
    if (!writeTarget || !clonedButton) return;

    const subtotalCents = getSubtotalCents();
    const routeCents    = getRoutePriceCents();
    if (subtotalCents === null || routeCents === null) return;

    const formatted = formatLikeSubtotal(subtotalCents + routeCents);
    if (formatted === lastKnownGood) return;

    if (guardObserver) guardObserver.disconnect();
    writeTarget.textContent = formatted;
    lastKnownGood           = formatted;
    clonedButton.setAttribute(PRICE_CORRECTED_ATTR, ''); // Reveal the price span
    attachGuard();

    const prefix = getButtonPrefix(clonedButton);
    clonedButton.setAttribute('aria-label', prefix ? `${prefix} ${formatted}` : formatted);
  }

  // ─── innerHTML interception ─────────────────────────────────────────────────

  function interceptInnerHTML(clonedBtn) {
    if (clonedBtn._routeInnerHTMLIntercepted) return;
    clonedBtn._routeInnerHTMLIntercepted = true;

    const nativeDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(clonedBtn, 'innerHTML', {
      set(value) {
        nativeDescriptor.set.call(this, value);
        reconcile();
      },
      get() {
        return nativeDescriptor.get.call(this);
      },
      configurable: true,
      enumerable: true,
    });
  }

  // ─── Targeted source observers ──────────────────────────────────────────────

  function watchSubtotal() {
    const subtotalEl = document.querySelector(SUBTOTAL_SOURCE_SELECTOR);
    if (!subtotalEl) return;
    if (subtotalObserver) subtotalObserver.disconnect();

    subtotalObserver = new MutationObserver(() => reconcile());
    subtotalObserver.observe(subtotalEl, { characterData: true, childList: true, subtree: true });
  }

  function watchRoutePrice() {
    const routePriceEl = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!routePriceEl) return;
    if (routePriceObserver) routePriceObserver.disconnect();

    routePriceObserver = new MutationObserver(() => reconcile());
    routePriceObserver.observe(routePriceEl, { characterData: true, childList: true, subtree: true });
  }

  // ─── Cloned button setup ────────────────────────────────────────────────────

  function processClonedButton(btn) {
    if (btn._routeSubtotalProcessed) return;
    btn._routeSubtotalProcessed = true;

    interceptInnerHTML(btn);
    attachGuard();
    watchSubtotal();
    watchRoutePrice();
    reconcile();
  }

  // ─── Outer observer ─────────────────────────────────────────────────────────

  function attachObserver() {
    const root = document.querySelector(OBSERVER_ROOT_SELECTOR);
    if (!root) return;
    if (outerObserver) outerObserver.disconnect();

    outerObserver = new MutationObserver(() => {
      const btn = document.querySelector(CLONED_BUTTON_SELECTOR);
      if (btn) processClonedButton(btn);
    });

    outerObserver.observe(root, { childList: true, subtree: true });

    const btn = document.querySelector(CLONED_BUTTON_SELECTOR);
    if (btn) processClonedButton(btn);
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  function waitForRoot() {
    if (
      document.getElementById(ROUTE_ROOT_ID) &&
      document.querySelector(OBSERVER_ROOT_SELECTOR)
    ) {
      attachObserver();
      return;
    }

    let initTimeout = null;

    const bodyObserver = new MutationObserver(() => {
      if (!document.getElementById(ROUTE_ROOT_ID)) return;
      if (!document.querySelector(OBSERVER_ROOT_SELECTOR)) return;
      bodyObserver.disconnect();
      clearTimeout(initTimeout);
      attachObserver();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
    initTimeout = setTimeout(() => bodyObserver.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRoot);
  } else {
    waitForRoot();
  }

  // ─── bfcache ────────────────────────────────────────────────────────────────

  window.addEventListener('pagehide', () => {
    if (outerObserver) outerObserver.disconnect();
    if (guardObserver) guardObserver.disconnect();
    if (subtotalObserver) subtotalObserver.disconnect();
    if (routePriceObserver) routePriceObserver.disconnect();
  });

  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    attachObserver();
    const btn = document.querySelector(CLONED_BUTTON_SELECTOR);
    if (btn) {
      btn._routeSubtotalProcessed = false;
      processClonedButton(btn);
    }
  });
})();